import { useState, useEffect } from 'react';
import { Airport } from '../../game/airport';
import { DATA } from '../../game/data';
import { IMAGES } from '../../game/data/images';
import { observer } from 'mobx-react-lite';
import { COLORS } from '../../game/data/colors';
import { ManagerProgressBar } from '../UI/Reusable/ManagerProgressBar';
import { Airplane } from '../../game/airplane';
import { tryStartAudio } from '../../game/audio';

const SHAPE_SIZE_REM = 2.2;

export const AirportView: React.FC<{ airport: Airport }> = observer(({ airport }) => {
  if (!airport.isAvailable) {
    return null;
  }

  const renderAirplanes = () => {
    const onAirplaneClick = (a: Airplane) => {
      tryStartAudio();
      if (DATA.tutorialStep === 'step3') {
        DATA.setTutorialStep('done');
      }
      a.quickTrip();
    };

    const airplanes = airport.getAirplanes();
    return (
      <div
        style={{
          position: 'absolute',
          transform: 'translateX(-50%)',
          left: '50%',
          bottom: 'calc(100% + 8px)',
          width: 80,
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {airplanes.map((airplane) => (
          <div key={airplane.id} className="airport-airplane" onClick={() => onAirplaneClick(airplane)}>
            <img src={IMAGES.AIRPLANES[airplane.color]} alt="airplane" />
          </div>
        ))}
      </div>
    );
  };

  const renderPopulationAndManager = () => {
    let filter = 'none';
    if (airport.passengerCount > airport.getUpgradeValue('capacity') * 0.85) {
      filter = 'brightness(0) saturate(100%) invert(89%) sepia(97%) saturate(601%) hue-rotate(323deg) brightness(100%) contrast(98%)';
    }
    if (airport.isFull) {
      filter = 'brightness(0) saturate(100%) invert(61%) sepia(44%) saturate(1983%) hue-rotate(315deg) brightness(98%) contrast(97%)';
    }

    const manager = DATA.managers.find((m) => m.airportSlug === airport.slug);

    return (
      <div
        style={{
          position: 'absolute',
          transform: 'translateX(-50%)',
          left: '50%',
          top: 'calc(100% + 4px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', filter, fontWeight: 600 }}>
          <img src={IMAGES.ICONS.person} alt="person" style={{ height: '1.2rem', marginRight: 2 }} />
          {airport.passengerCount}
        </div>
        {manager && <ManagerProgressBar height={6} manager={manager} />}
      </div>
    );
  };

  const onAirportClick = () => {
    tryStartAudio();
    DATA.showPlanTripPopup(airport.slug);
    if (DATA.tutorialStep === 'step1') DATA.setTutorialStep('step2');
  };

  const shadowSize = SHAPE_SIZE_REM * 0.5;

  return (
    <div
      id={`airport${airport.slug}`}
      style={{
        position: 'absolute',
        top: `calc(${airport.pos.y}% - ${SHAPE_SIZE_REM / 2}rem)`,
        left: `calc(${airport.pos.x}% - ${SHAPE_SIZE_REM / 2}rem)`,
        width: SHAPE_SIZE_REM + 'rem',
        height: SHAPE_SIZE_REM + 'rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '0',
          height: '0',
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          boxShadow: airport.isPopulationIncreaseEvent ? `0 0 ${shadowSize}rem ${shadowSize}rem rgb(0 255 45)` : 'none',
          transition: 'box-shadow 0.5s',
        }}
      ></div>
      {renderPopulationAndManager()}
      <div
        className="airport-img"
        style={{
          borderRadius: `50%`,
          backgroundColor: COLORS.zinc_900,
          border: `2px solid ${COLORS.white}`,
          overflow: 'hidden',
        }}
        onClick={onAirportClick}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            right: 0,
            top: `${(1 - airport.passengerCount / airport.getUpgradeValue('capacity')) * 100}%`,
            backgroundColor: COLORS.zinc_800,
          }}
        ></div>
      </div>
      {renderAirplanes()}
    </div>
  );
});
